
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDesignations,
  fetchLocations,
  fetchTasksList,
  fetchEmployees,
  createTaskSubmit
} from "../redux/actions/CreateTasksAdminActions";
import {
  FiPlus,
  FiTrash2,
  FiCalendar,
  FiClock,
  FiUser,
  FiMapPin,
  FiTarget,
  FiMessageSquare,
  FiCheck,
  FiArrowLeft
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreateTasksAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    designations, // This could be array of strings OR objects
    locations,
    tasksList,
    employeesByDesignation,
    loading,
    error,
    success
  } = useSelector(state => state.taskAdmin);

  const [tasks, setTasks] = useState([
    {
      designation: "",
      employees: [],
      user_id: "",
      task_name: "",
      start_time: "",
      end_time: "",
      location_id: "",
      other_location: "",
      remarks: "",
      createTaskDate: "",
      endTaskDate: ""
    }
  ]);

  // Set default dates
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 7); // Default 1 week duration
    const defaultEndDateStr = defaultEndDate.toISOString().split('T')[0];

    setTasks(prev => prev.map(task => ({
      ...task,
      createTaskDate: task.createTaskDate || today,
      endTaskDate: task.endTaskDate || defaultEndDateStr,
      start_time: task.start_time || "09:00",
      end_time: task.end_time || "17:00"
    })));
  }, []);

  // Fetch dropdown data
  useEffect(() => {
    dispatch(fetchDesignations());
    dispatch(fetchLocations());
    dispatch(fetchTasksList());
  }, [dispatch]);

  // Reset form on success
  useEffect(() => {
    if (success) {
      const today = new Date().toISOString().split('T')[0];
      const defaultEndDate = new Date();
      defaultEndDate.setDate(defaultEndDate.getDate() + 7);
      const defaultEndDateStr = defaultEndDate.toISOString().split('T')[0];

      setTasks([
        {
          designation: "",
          employees: [],
          user_id: "",
          task_name: "",
          start_time: "09:00",
          end_time: "17:00",
          location_id: "",
          other_location: "",
          remarks: "",
          createTaskDate: today,
          endTaskDate: defaultEndDateStr
        }
      ]);
    }
  }, [success]);

  const handleChange = (index, field, value) => {
    const updated = [...tasks];
    updated[index][field] = value;

    if (field === "designation") {
      updated[index].user_id = "";
      const empList = employeesByDesignation[value] || [];
      updated[index].employees = empList;
      setTasks(updated);

      if (!empList.length && value) dispatch(fetchEmployees(value));
    } else {
      setTasks(updated);
    }
  };

  const addTask = () => {
    const today = new Date().toISOString().split('T')[0];
    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 7);
    const defaultEndDateStr = defaultEndDate.toISOString().split('T')[0];

    setTasks([
      ...tasks,
      {
        designation: "",
        employees: [],
        user_id: "",
        task_name: "",
        start_time: "09:00",
        end_time: "17:00",
        location_id: "",
        other_location: "",
        remarks: "",
        createTaskDate: today,
        endTaskDate: defaultEndDateStr
      }
    ]);
  };

  const removeTask = (index) => setTasks(tasks.filter((_, i) => i !== index));

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate dates
    for (let task of tasks) {
      if (new Date(task.endTaskDate) < new Date(task.createTaskDate)) {
        alert("End date cannot be before start date");
        return;
      }
    }

    const now = new Date();
    const autoDate = now.toISOString().split("T")[0];
    const autoTime = now.toTimeString().slice(0, 5);

    const payload = tasks.map(task => ({ ...task, date: autoDate, time: autoTime }));
    dispatch(createTaskSubmit(payload));
  };

  const isFormValid = tasks.every(task => 
    task.designation && 
    task.user_id && 
    task.task_name && 
    task.location_id && 
    task.createTaskDate && 
    task.endTaskDate && 
    task.start_time && 
    task.end_time
  );

  // Helper function to get designation display value
  const getDesignationValue = (item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object' && item.name) return item.name;
    return String(item);
  };

  // Helper function to get designation key
  const getDesignationKey = (item, index) => {
    if (typeof item === 'string') return `${item}-${index}`;
    if (item && typeof item === 'object' && item.id) return item.id;
    return index;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Creating Tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/task")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Tasks
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiTarget className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Create Tasks
                </h1>
                <p className="text-blue-100">
                  Assign and schedule tasks for your team members
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      Tasks created successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      All tasks have been assigned to the selected employees.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiTarget className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {tasks.map((task, index) => (
                <div
                  key={`task-${index}`}
                  className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">{index + 1}</span>
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Task #{index + 1}
                      </h2>
                    </div>
                    {tasks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTask(index)}
                        className="flex items-center gap-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 p-2 rounded-lg transition-colors"
                      >
                        <FiTrash2 className="text-sm" />
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Designation - HANDLES BOTH STRINGS AND OBJECTS */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <FiUser className="text-sm" />
                          Designation
                        </div>
                      </label>
                      <select
                        className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                        value={task.designation}
                        onChange={(e) => handleChange(index, "designation", e.target.value)}
                        required
                      >
                        <option value="">Select Designation</option>
                        {Array.isArray(designations) && designations.map((item, i) => (
                          <option key={getDesignationKey(item, i)} value={getDesignationValue(item)}>
                            {getDesignationValue(item)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* User */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <FiUser className="text-sm" />
                          Assign To
                        </div>
                      </label>
                      <select
                        className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                        value={task.user_id || ""}
                        onChange={(e) => handleChange(index, "user_id", Number(e.target.value))}
                        required
                        disabled={!task.designation}
                      >
                        <option value="">Select Employee</option>
                        {(employeesByDesignation[task.designation] || []).map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name} ({emp.phone})
                          </option>
                        ))}
                      </select>
                      {!task.designation && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Select designation first
                        </p>
                      )}
                    </div>

                    {/* Task Name */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Task Name
                      </label>
                      <input
                        type="text"
                        className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                        value={task.task_name}
                        onChange={(e) => handleChange(index, "task_name", e.target.value)}
                        placeholder="Enter task name"
                        required
                      />
                    </div>

                    {/* Visit Location */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <FiMapPin className="text-sm" />
                          Visit Location
                        </div>
                      </label>
                      <select
                        className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                        value={task.other_location || ""}
                        onChange={(e) => handleChange(index, "other_location", Number(e.target.value))}
                      >
                        <option value="">Select Location</option>
                        {Array.isArray(locations) && locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.location_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Visit Purpose */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <FiTarget className="text-sm" />
                          Visit Purpose
                        </div>
                      </label>
                      <select
                        className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                        value={task.location_id || ""}
                        onChange={(e) => handleChange(index, "location_id", Number(e.target.value))}
                        required
                      >
                        <option value="">Select Purpose</option>
                        {Array.isArray(tasksList) && tasksList.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Start Date & Time */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <FiCalendar className="text-sm" />
                          Start Date & Time
                        </div>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          className="bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                          value={task.createTaskDate}
                          onChange={(e) => handleChange(index, "createTaskDate", e.target.value)}
                          required
                        />
                        <input
                          type="time"
                          className="bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                          value={task.start_time}
                          onChange={(e) => handleChange(index, "start_time", e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    {/* End Date & Time */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <FiClock className="text-sm" />
                          End Date & Time
                        </div>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          className="bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                          value={task.endTaskDate}
                          onChange={(e) => handleChange(index, "endTaskDate", e.target.value)}
                          required
                        />
                        <input
                          type="time"
                          className="bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                          value={task.end_time}
                          onChange={(e) => handleChange(index, "end_time", e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    {/* Remarks */}
                    <div className="md:col-span-2 lg:col-span-3 space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-2">
                          <FiMessageSquare className="text-sm" />
                          Remarks
                        </div>
                      </label>
                      <textarea
                        className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400 h-20"
                        value={task.remarks}
                        onChange={(e) => handleChange(index, "remarks", e.target.value)}
                        placeholder="Additional notes or instructions..."
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={addTask}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors"
                >
                  <FiPlus className="text-lg" />
                  Add Another Task
                </button>
                
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Tasks...
                    </>
                  ) : (
                    <>
                      <FiCheck className="text-lg" />
                      Submit All Tasks
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Form Tips */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            💡 Task Creation Guidelines
          </h3>
          <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <li>• Select the appropriate designation to see available employees</li>
            <li>• Set realistic start and end dates for each task</li>
            <li>• Provide clear task names and purposes for better understanding</li>
            <li>• Add specific locations if the task requires on-site work</li>
            <li>• Use remarks for additional instructions or special requirements</li>
            <li>• All fields marked with * are required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateTasksAdmin;